import { useQuery } from "@tanstack/react-query";
import { supabaseAgency } from "@/lib/supabase";

export type ActionSourceType =
  | "email"
  | "task"
  | "linear_issue"
  | "meeting"
  | "alert"
  | "job_run"
  | "snapshot"
  | "knowledge";

export type ActionStatus = "active" | "handled" | "snoozed" | "dismissed" | "stale";
export type ActionUrgency = "critical" | "high" | "normal" | "low";

export interface ActionItem {
  item_id: string;
  source_type: ActionSourceType;
  source_id: string;
  client: string | null;
  title: string;
  summary: string | null;
  reason: string;
  priority: number;
  urgency: ActionUrgency;
  status: ActionStatus;
  snoozed_until: string | null;
  source_url: string | null;
  deep_link_url: string | null;
  context: Record<string, unknown> | null;
  first_seen_at: string;
  last_seen_at: string;
  synced_at: string;
}

export interface ActionEvent {
  id: string;
  item_id: string;
  action_id: string | null;
  action_type: string;
  actor: string | null;
  status: string;
  detail: Record<string, unknown> | null;
  created_at: string;
}

export function useActionItems() {
  return useQuery({
    queryKey: ["action_items"],
    queryFn: async (): Promise<ActionItem[]> => {
      const { data, error } = await supabaseAgency
        .from("os_action_items")
        .select("*")
        .in("status", ["active", "snoozed"])
        .order("priority", { ascending: true })
        .order("last_seen_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ActionItem[];
    },
    refetchInterval: 60_000,
  });
}

export function useActionEvents(itemId?: string | null) {
  return useQuery({
    queryKey: ["action_events", itemId],
    enabled: Boolean(itemId),
    queryFn: async (): Promise<ActionEvent[]> => {
      const { data, error } = await supabaseAgency
        .from("os_action_events")
        .select("*")
        .eq("item_id", itemId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as ActionEvent[];
    },
  });
}
